/**
 * SPEC-AgentUI — ResolutionPanel (Step 1 reason + Step 2 action + initial note).
 *
 * Lives in two layouts:
 *   - "form" — pre-save Bad. Step 1 / Step 2 / initial note / Save.
 *   - "log"  — post-save. Notes timeline + add-note row + status mutations.
 *
 * Which layout to render is decided by `deriveResolutionPanelState` in the
 * shared helpers. This component renders the form layout; the log layout is
 * `AgentResolutionLog` below.
 */
import { useState } from "react";
import {
  BAD_REASONS_UI,
  INITIAL_ACTIONS_UI,
  type BadReasonUi,
  type InitialActionUi,
  decisionBodyError,
} from "@/lib/agent-ui-helpers";
import { Starling } from "@/lib/starling-brand";
import { qaVar, tint } from "@/lib/qa-theme";

// Theme variables (--qa-* in index.css) so the panel follows dark mode.
// `bad` / `purple` are fills and borders; the *Text keys are for text.
const TOKENS = {
  card: "var(--qa-card)",
  border: "var(--qa-border)",
  text: "var(--qa-text)",
  bad: "var(--qa-bad)",
  badText: "var(--qa-bad-text)",
  badBg: "var(--qa-bad-bg)",
  badBorder: "var(--qa-bad-border)",
  purple: qaVar("purple"),
  purpleText: qaVar("purple-text"),
  purpleSub: tint(qaVar("purple"), 0x1f),
  purpleSubB: tint(qaVar("purple"), 0x2e),
  txt2: "var(--qa-text-2)",
  txt3: "var(--qa-text-3)",
} as const;

// SPEC-AgentDetail-Revisions §"Resolution panel + log copy" — locked labels.
// Reasons: title + optional subtitle. Bag Notes and Other have no subtitle.
const REASON_LABEL: Record<BadReasonUi, { title: string; sub: string | null }> = {
  mistake: { title: "Mistake", sub: "Mismatch in POD" },
  cant_verify: { title: "Can't Verify", sub: "Need More Info" },
  bag_notes: { title: "Bag Notes", sub: null },
  damage: { title: "Damage", sub: "Product Damaged" },
  other: { title: "Other", sub: null },
};

// Actions: phone + text get inline icons; Other is plain.
const ACTION_LABEL: Record<InitialActionUi, { icon: string; title: string }> = {
  phone: { icon: "📞", title: "Calling" },
  text: { icon: "💬", title: "Texting" },
  other: { icon: "", title: "Other" },
};

export interface ResolutionPanelProps {
  onSave: (input: {
    badReason: BadReasonUi;
    badReasonOther?: string;
    /** Absent only when `hideInitialAction` is set (Training Simulator). */
    initialAction?: InitialActionUi;
    initialActionOther?: string;
    initialNote?: string;
  }) => void;
  onUndo: () => void;
  saving?: boolean;
  /**
   * Training Simulator, Phases 3–4 (Sarah, 2026-09-15): the trainee is graded
   * on the reason, not on what they would do next, so Step 2 is not shown and
   * Save needs only the reason. Live agents never set this.
   */
  hideInitialAction?: boolean;
  /**
   * Training Simulator (Sarah, 2026-09-15): a trainee who says Bad must write
   * why — the note becomes "Why is it Bad?" and Save waits for it. Live
   * agents keep the note optional.
   */
  requireNote?: boolean;
}

export function AgentResolutionPanel({ onSave, onUndo, saving, hideInitialAction = false, requireNote = false }: ResolutionPanelProps) {
  const [badReason, setBadReason] = useState<BadReasonUi | null>(null);
  const [badReasonOther, setBadReasonOther] = useState("");
  const [initialAction, setInitialAction] = useState<InitialActionUi | null>(null);
  const [initialActionOther, setInitialActionOther] = useState("");
  const [initialNote, setInitialNote] = useState("");

  const err = decisionBodyError({
    decision: "BAD",
    badReason,
    badReasonOther,
    initialAction,
    initialActionOther,
    initialActionRequired: !hideInitialAction,
    initialNote,
    noteRequired: requireNote,
  });

  return (
    <div
      data-testid="agent-resolution-panel"
      style={{
        background: TOKENS.card,
        border: `1px solid ${TOKENS.badBorder}`,
        borderLeft: `3px solid ${TOKENS.bad}`,
        borderRadius: 8,
        padding: "16px 18px",
        marginBottom: 12,
        boxShadow: "0 1px 2px var(--qa-shadow)",
        fontFamily: Starling.fontBody,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 14, fontWeight: 600, color: TOKENS.badText }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: TOKENS.bad }} />
          Why bad?
        </div>
        <button
          type="button"
          onClick={onUndo}
          data-testid="agent-resolution-undo"
          style={{
            fontSize: 11,
            color: TOKENS.txt2,
            background: "none",
            border: "none",
            textDecoration: "underline",
            cursor: "pointer",
            fontFamily: Starling.fontBody,
          }}
        >
          Undo — back to Good/Bad
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 14, marginBottom: 14 }}>
        <div>
          <div style={stepStyle}>{hideInitialAction ? "Reason" : "Step 1 — Reason"}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 6 }}>
            {BAD_REASONS_UI.map((r) => {
              const selected = badReason === r;
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => setBadReason(r)}
                  data-testid={`agent-bad-reason-${r}`}
                  aria-pressed={selected}
                  style={{
                    ...chipStyle(selected, "bad"),
                  }}
                >
                  {REASON_LABEL[r].title}
                  {REASON_LABEL[r].sub && (
                    <small style={chipSubStyle(selected, "bad")}>{REASON_LABEL[r].sub}</small>
                  )}
                </button>
              );
            })}
          </div>
          {badReason === "other" && (
            <input
              data-testid="agent-bad-reason-other-input"
              placeholder="Describe the reason"
              value={badReasonOther}
              onChange={(e) => setBadReasonOther(e.target.value)}
              style={inputStyle}
            />
          )}
        </div>
        {!hideInitialAction && (
        <div>
          <div style={stepStyle}>Step 2 — Immediate Action</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {INITIAL_ACTIONS_UI.map((a) => {
              const selected = initialAction === a;
              const label = ACTION_LABEL[a];
              return (
                <button
                  key={a}
                  type="button"
                  onClick={() => setInitialAction(a)}
                  data-testid={`agent-initial-action-${a}`}
                  aria-pressed={selected}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    ...chipStyle(selected, "purple"),
                  }}
                >
                  {label.icon && <span style={{ fontSize: 14 }}>{label.icon}</span>}
                  {label.title}
                </button>
              );
            })}
          </div>
          {initialAction === "other" && (
            <input
              data-testid="agent-initial-action-other-input"
              placeholder="Describe the action"
              value={initialActionOther}
              onChange={(e) => setInitialActionOther(e.target.value)}
              style={inputStyle}
            />
          )}
        </div>
        )}
      </div>

      <div style={{ marginBottom: 14 }}>
        <div style={stepStyle}>{requireNote ? "Why is it Bad? (required)" : "Initial Note (Optional)"}</div>
        <textarea
          data-testid="agent-initial-note-input"
          placeholder={requireNote ? "Say what is wrong with this delivery — e.g. the bag is at a different unit door than the one on the task." : "e.g. Calling driver to ask him to go back to the unit."}
          value={initialNote}
          onChange={(e) => setInitialNote(e.target.value)}
          style={{
            width: "100%",
            minHeight: 64,
            border: `1px solid ${TOKENS.border}`,
            borderRadius: 6,
            padding: "8px 10px",
            fontFamily: Starling.fontBody,
            fontSize: 13,
            resize: "vertical",
            background: TOKENS.card,
            color: TOKENS.text,
          }}
        />
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          borderTop: `1px solid ${TOKENS.border}`,
          paddingTop: 12,
          marginTop: 6,
        }}
      >
        {err && (
          <span style={{ fontSize: 11, color: TOKENS.badText, fontWeight: 600 }} data-testid="agent-resolution-err">
            {err}
          </span>
        )}
        <span style={{ flex: 1 }} />
        <button
          type="button"
          onClick={() => {
            if (err || !badReason || (!hideInitialAction && !initialAction)) return;
            onSave({
              badReason,
              badReasonOther: badReason === "other" ? badReasonOther.trim() : undefined,
              initialAction: hideInitialAction ? undefined : (initialAction ?? undefined),
              initialActionOther: !hideInitialAction && initialAction === "other" ? initialActionOther.trim() : undefined,
              initialNote: initialNote.trim() || undefined,
            });
          }}
          disabled={!!err || !!saving}
          data-testid="agent-resolution-save"
          style={{
            background: TOKENS.bad,
            color: "#fff",
            border: "none",
            padding: "10px 18px",
            borderRadius: 6,
            fontFamily: Starling.fontBody,
            fontSize: 13,
            fontWeight: 700,
            cursor: err || saving ? "not-allowed" : "pointer",
            opacity: err || saving ? 0.6 : 1,
          }}
        >
          Save & Keep Working
        </button>
      </div>
    </div>
  );
}

const stepStyle: React.CSSProperties = {
  fontSize: 10,
  color: TOKENS.txt3,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  fontWeight: 700,
  marginBottom: 6,
};

const inputStyle: React.CSSProperties = {
  marginTop: 8,
  width: "100%",
  border: `1px solid ${TOKENS.border}`,
  borderRadius: 6,
  padding: "6px 10px",
  fontFamily: Starling.fontBody,
  fontSize: 13,
  background: TOKENS.card,
  color: TOKENS.text,
};

function chipStyle(selected: boolean, accent: "bad" | "purple"): React.CSSProperties {
  if (selected) {
    return accent === "bad"
      ? {
          background: TOKENS.badBg,
          border: `1px solid ${TOKENS.bad}`,
          color: TOKENS.badText,
          padding: "8px 6px",
          borderRadius: 6,
          fontSize: 12,
          textAlign: "center",
          fontWeight: 600,
          lineHeight: 1.25,
          cursor: "pointer",
          fontFamily: Starling.fontBody,
        }
      : {
          background: TOKENS.purpleSub,
          border: `1px solid ${TOKENS.purple}`,
          color: TOKENS.purpleText,
          padding: "8px 12px",
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
          fontFamily: Starling.fontBody,
        };
  }
  return {
    background: TOKENS.card,
    border: `1px solid ${TOKENS.border}`,
    color: TOKENS.text,
    padding: accent === "bad" ? "8px 6px" : "8px 12px",
    borderRadius: 6,
    fontSize: 12,
    textAlign: accent === "bad" ? "center" : "left",
    fontWeight: 600,
    lineHeight: 1.25,
    cursor: "pointer",
    fontFamily: Starling.fontBody,
  };
}

function chipSubStyle(selected: boolean, _accent: "bad" | "purple"): React.CSSProperties {
  return {
    display: "block",
    fontSize: 9.5,
    color: selected ? TOKENS.badText : TOKENS.txt3,
    opacity: selected ? 0.85 : 1,
    fontWeight: 500,
    marginTop: 2,
    letterSpacing: "0.01em",
  };
}
